import {
  DATE_FORMAT_FULL_MONTH_YEAR_FNS,
  FilterKeys,
  LastActivityEntry,
  MessageForGroup,
  MessageStatus,
  MessageType,
  RootEntityType,
  ThreadLastActivityInputRootEntityTypeEnum,
  useGetMessagesQuery,
  useGetThreadsLastActivityQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useUserNotifications } from '@schooly/context';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import { useMemo, useRef, useState } from 'react';

import { retrieveApiDate } from '../../../../helpers/misc';
import { format } from 'date-fns';
import { useNotifications } from '@schooly/components/notifications';

const MONTH_NONE = 'NONE';

export const useThreadsSidebarData = () => {
  const { relationId = '', schoolId = '' } = useAuth();
  const { threadsUnread } = useUserNotifications();
  const { showError } = useNotifications();

  const { data, fetchNextPage, isFetching, isLoading, hasNextPage, isFetchingNextPage } =
    useGetMessagesQuery(
      {
        school_id: schoolId,
        filters: {
          [FilterKeys.Creator]: [relationId],
          [FilterKeys.MessagesStatus]: [MessageStatus.Published],
          [FilterKeys.MessagesType]: [MessageType.Thread],
        },
      },
      { enabled: !!relationId, refetchOnMount: 'always' },
    );

  const entries = useMemo(
    () =>
      (data?.pages.reduce<MessageForGroup[]>((prev, curr) => [...prev, ...curr.results], []) ?? [])
        .filter((m) => m.type === MessageType.Thread)
        .sort((a, b) => (b.publish_date ?? 0) - (a.publish_date ?? 0)),
    [data?.pages],
  );

  const [activityMap, setActivityMap] = useState<Record<string, LastActivityEntry>>({});
  const fetchedIdsRef = useRef(new Set<string>());

  const newIds = useMemo(
    () => entries.map((m) => m.id).filter((id) => !fetchedIdsRef.current.has(id)),
    [entries],
  );

  const { isFetching: isActivityFetching } = useGetThreadsLastActivityQuery(
    {
      school_id: schoolId,
      root_entity_type: ThreadLastActivityInputRootEntityTypeEnum.Message,
      root_entity_ids: newIds,
    },
    {
      enabled: newIds.length > 0,
      refetchOnMount: 'always',
      onSuccess: (data) => {
        setActivityMap((prev) => ({ ...prev, ...data.data }));
        for (const id of newIds) fetchedIdsRef.current.add(id);
      },
      onError: showError,
    },
  );

  const loadingActivityIds = useMemo(() => {
    if (!isActivityFetching) return new Set<string>();
    const ids = new Set<string>();
    for (const e of entries) {
      if (!fetchedIdsRef.current.has(e.id)) ids.add(e.id);
    }
    return ids;
  }, [entries, isActivityFetching]);

  const unreadMessageIds = useMemo(() => {
    const ids = new Set<string>();
    const threadData = threadsUnread?.unread_threads?.[RootEntityType.MESSAGE];
    if (threadData) {
      for (const messageId of Object.keys(threadData)) {
        ids.add(messageId);
      }
    }
    return ids;
  }, [threadsUnread]);

  const threadsByMonthAndYear = useMemo(() => {
    return entries.reduce<Record<string, MessageForGroup[]>>((acc, message) => {
      const date = message.publish_date ? retrieveApiDate(message.publish_date) : undefined;
      const monthAndYear = date
        ? format(date, DATE_FORMAT_FULL_MONTH_YEAR_FNS).toUpperCase()
        : MONTH_NONE;

      acc[monthAndYear] = acc[monthAndYear] ? [...acc[monthAndYear], message] : [message];
      return acc;
    }, {});
  }, [entries]);

  const loaderRef = useInfiniteScroll(isFetching || isFetchingNextPage, fetchNextPage, hasNextPage);
  const count = entries.length;
  const isEmpty = !isLoading && count === 0;

  return {
    threadsByMonthAndYear,
    unreadMessageIds,
    activityMap,
    loadingActivityIds,
    isLoading,
    count,
    isEmpty,
    loaderRef,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  };
};
